import { create } from 'zustand';
import { ACTION_SORT_TAG } from '../../core/api/action-sort';
import { apiUrl } from '../../core/api/api-url';
import { withQuery } from '../../core/api/query';
import { toApiError, type ApiError } from '../../core/api/api-error';
import { appClient } from '../../core/api/http';
import { dismissRefused, submitCapture } from '../../core/outbox/outbox.store';
import { createMountGuard } from '../../core/reach/mount-guard';
import { createQuietRefresh } from '../../core/reach/quiet-refresh';
import {
  restoreLastCleared, shownFromStorageNow, useShownFromStorage, type StoredViewSummary,
} from '../../core/stored-views/presentation.store';
import { useWritable } from '../../core/stored-views/use-writable';
import { recordPageOpened } from '../../core/stored-views/last-opened';
import { completedFetchSize } from '../../core/preferences/defaults';
import { completionsShown, effectiveZone } from '../../core/preferences/preferences.store';
import { clock } from '../../core/time/clock';
import { calendarDayIn } from '../../core/time/calendar-day';
import type {
  ActionSummary, ContextSummary, PagedActions,
  ProjectSummary, QuickAddRequest, QuickCaptureResult,
} from '../../core/api/types';
import type { ContainerKind } from '../../shared/actions/model/action-row';
import { sectionsOf, type ActionSection } from '../../shared/actions/model/sections';
import { foldIn, mutate, type OptimisticHost, type Reread } from '../../shared/actions/optimistic';
import { submitOrWithdraw } from '../../shared/actions/queued-change';
import { containerLookupsReader, lookupsOf, type ContainerLookups } from '../../shared/containers/container-lookups';

/**
 * The all-open-actions read's page size. FR-005: this store's whole job is a *fixed* request
 * count regardless of how many actions the container holds, the same reasoning as `home.store.ts`'s
 * `MAX_PAGE` — asking for the maximum in one request rather than a page per screenful.
 */
const MAX_PAGE = 500;

/**
 * Not `ProjectSummary | ContextSummary` alone: a bare union loses the correlation between `kind` and
 * which container type the page is showing, so a caller narrowing on `data.kind` gets the matching
 * `container` for free instead of an `as`.
 *
 * **Both container lists are carried by both arms** (016). Until 016 each arm carried only the
 * *other* list — contexts for a project (row labels and the default-context picker), projects for a
 * context (labelling an on-hold action) — which was exactly what the page displayed. The row's editor
 * offers both pickers wherever it opens (FR-002/FR-003), so a project page now needs its projects
 * list too and a context page its contexts. That is one more fixed read per load, not one per action,
 * and it is `shared/containers/container-lookups.ts`'s single statement of the two reads rather than
 * a second spelling of them here.
 */
export type ContainerData =
  | {
      readonly kind: 'project';
      readonly container: ProjectSummary;
      /** In the API's order (FR-017/006 precedent) — never re-sorted. Every open status, never `completed`
       * (`view=all`'s predicate is `completed_at IS NULL`); `sections.ts` partitions it into the four sections. */
      readonly actions: readonly ActionSummary[];
      readonly completedRecent: readonly ActionSummary[];
      readonly contexts: readonly ContextSummary[];
      readonly projects: readonly ProjectSummary[];
    }
  | {
      readonly kind: 'context';
      readonly container: ContextSummary;
      readonly actions: readonly ActionSummary[];
      readonly completedRecent: readonly ActionSummary[];
      readonly contexts: readonly ContextSummary[];
      readonly projects: readonly ProjectSummary[];
    };

/**
 * One value, so the view never has to reason about which fields are meaningful — the same shape
 * as `home.store.ts`'s `HomeStatus`, plus `not-found`: a stale link to a deleted container is not the
 * same failure as the API being unreachable, and research R-005 says the page renders the shared
 * `NotFound` component in its place rather than an error banner.
 */
export type ContainerStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly data: ContainerData | null }
  | { readonly kind: 'ready'; readonly data: ContainerData }
  | { readonly kind: 'error'; readonly error: ApiError; readonly data: ContainerData | null }
  | { readonly kind: 'not-found' };

function dataOf(status: ContainerStatus): ContainerData | null {
  switch (status.kind) {
    case 'ready':
    case 'loading':
    case 'error':
      return status.data;
    default:
      return null;
  }
}

/**
 * A settings write's body, shaped so TypeScript's own "omitted optional key" *is* the merge-patch
 * "leave alone" — the caller never has to reach for a sentinel: skip the key to leave it, pass
 * `null` on a nullable field to clear it (R-006), pass a value to change it. `updateProjectSettings`
 * forwards the object as-is (no full-record spread, no `undefined`-valued placeholders), so what
 * `JSON.stringify` puts on the wire has exactly the keys the caller set — never more.
 */
export interface ProjectSettingsPatch {
  readonly name?: string;
  readonly description?: string | null;
  readonly defaultContextId?: number | null;
  /**
   * The three states `PatchProjectRequest` accepts, not the bare `string` `ProjectSummary.state`
   * carries — a patch body is written here, so it can be exact where a read has to take what 004's
   * generated types give it.
   */
  readonly state?: 'active' | 'hidden' | 'completed';
}

export interface ContextSettingsPatch {
  readonly name?: string;
  readonly isActive?: boolean;
}

interface ContainerState {
  readonly status: ContainerStatus;
  /**
   * The practitioner's calendar day, frozen at load: a row must not change colour mid-session
   * because a timer ticked. 010 FR-006 — reduced once, in `load()`, by `calendarDayIn`.
   */
  readonly today: Date;
  /** The instant `today` was reduced from, frozen with it. Staleness measures from here (010). */
  readonly now: Date;
  /** The last refused settings write. Separate from `status`: a refusal must not blank the page. */
  readonly problem: ApiError | null;
  /** Action ids with a row-level change in flight — shown as pending, not changed again until it lands. */
  readonly pending: readonly number[];
  /** The capture box's own busy state; see `home.store.ts`'s `HomeState.capturing`
   *  for the fuller comment. Independent of `status`. */
  readonly capturing: boolean;
  /**
   * 036 (issue 234) — the container `load()` was actually asked to fetch, set BEFORE either of its
   * reads is even attempted and independent of whether any of them ever answers. `quickAdd()` reads
   * this when `status.data` is `null` (nothing has ever loaded here, or the last load failed with
   * nothing to fall back to — `noStoredCopy` on the page): the practitioner is still looking at a
   * specific context or project, named by the route, and capture must not depend on that container's
   * own data having ever arrived (FR-015 — the server parses the line; this store does not need the
   * container to know which one it was typed against).
   */
  readonly requested: { readonly kind: ContainerKind; readonly id: number } | null;
}

const EMPTY: ContainerState = {
  status: { kind: 'idle' }, today: new Date(0), now: new Date(0), problem: null, pending: [], capturing: false,
  requested: null,
};

const store = create<ContainerState>()(() => EMPTY);

// --- reading -----------------------------------------------------------------------------------

const dataNow = (): ContainerData | null => dataOf(store.getState().status);

export const containerStatus = {
  use: (): ContainerStatus => store((s) => s.status),
  get: (): ContainerStatus => store.getState().status,
};
export const containerData = {
  use: (): ContainerData | null => store((s) => dataOf(s.status)),
  get: dataNow,
};
export const containerProblem = {
  use: (): ApiError | null => store((s) => s.problem),
  get: (): ApiError | null => store.getState().problem,
};
export const containerToday = {
  use: (): Date => store((s) => s.today),
  get: (): Date => store.getState().today,
};
export const containerNow = {
  use: (): Date => store((s) => s.now),
  get: (): Date => store.getState().now,
};

/** FR-007's four sections, built from `action.status` alone (research R-003) — see `shared/actions/model/sections.ts`. */
const sectionsFrom = (data: ContainerData | null): readonly ActionSection[] => (data ? sectionsOf(data.actions) : []);

export const containerSections = {
  use: (): readonly ActionSection[] => sectionsFrom(store((s) => dataOf(s.status))),
  get: (): readonly ActionSection[] => sectionsFrom(dataNow()),
};

/** 016 FR-002/FR-003: the two lists the row editor's pickers derive from (see `lookupsOf`). */
export const containerLookups = {
  use: (): ContainerLookups | null => lookupsOf(store((s) => dataOf(s.status))),
  get: (): ContainerLookups | null => lookupsOf(dataNow()),
};

export const isPending = (id: number): boolean => store.getState().pending.includes(id);

/** 036 T092 — a real subscription for `pending`; see `home.store.ts`'s `usePendingIds` for why this
 *  is needed now that `setCompleted`/`toggleStar`/`defer` can settle with no other state changing. */
export const usePendingIds = (): readonly number[] => store((s) => s.pending);

/** The capture box's own busy state; see `ContainerState.capturing`. */
export const containerCapturing = {
  use: (): boolean => store((s) => s.capturing),
  get: (): boolean => store.getState().capturing,
};

// --- writing -----------------------------------------------------------------------------------

const contextsUrl = (): string => apiUrl('/api/v1/contexts');
const projectsUrl = (): string => apiUrl('/api/v1/projects');
const actionsUrl = (): string => apiUrl('/api/v1/actions');

const idParamFor = (kind: ContainerKind): 'projectId' | 'contextId' => (kind === 'project' ? 'projectId' : 'contextId');

/**
 * 036 (issue 221, FR-021): this screen's four reads, for `core/stored-views/`'s allow-list and for
 * `useContainerWritable` below — the same three query-built requests `readActionsAll`/
 * `readActionsCompleted`/`readContainer` below issue, so this can never name a URL the screen does
 * not actually request.
 *
 * 036 (issue 259, C3) — the ONE place these three URLs are built. Before this fix each of the three
 * read functions below built its own copy of one of these, and a one-parameter drift between a read
 * and its matching entry here would have meant this screen's stored view was never found offline
 * (issue 259's own finding) — so each read now calls straight into this array instead.
 *
 * Exported since issue 222: `core/stored-views/fetch-ahead.ts` calls this for every context and
 * project it prefetches, rather than reconstructing these three requests a third time — the same
 * reason `tagViewUrls` below is exported for the identical caller.
 */
export function containerViewUrls(kind: ContainerKind, id: number): readonly string[] {
  return [
    kind === 'project' ? `${projectsUrl()}/${id}` : `${contextsUrl()}/${id}`,
    withQuery(actionsUrl(), { view: 'all', sort: ACTION_SORT_TAG, [idParamFor(kind)]: id, pageSize: MAX_PAGE }),
    withQuery(actionsUrl(), { view: 'completed', [idParamFor(kind)]: id, pageSize: completedFetchSize(completionsShown.get()) }),
  ];
}

/**
 * `GET /contexts/{id}` or `GET /projects/{id}` — research R-005; a 404 here is the not-found case.
 * `confirmingWrite`, same reasoning as `readActionsAll`'s own note — `rereadContainerFor` below
 * confirms a write that just happened.
 *
 * One call, not a branch on `kind` naming the same request twice: `request-read-once.spec.ts`
 * forbids the identical `appClient().get(...)` argument expression appearing at two call sites in
 * one `*.store.ts` file, which a `kind === 'project' ? get<ProjectSummary>(url, …) :
 * get<ContextSummary>(url, …)` shape would be once both branches read the SAME url from
 * `containerViewUrls` (036, issue 259, C3) — the union return type says exactly as much as the two
 * branches did.
 */
const readContainer = (kind: ContainerKind, id: number, confirmingWrite = false): Promise<ProjectSummary | ContextSummary> =>
  appClient().get<ProjectSummary | ContextSummary>(containerViewUrls(kind, id)[0]!, { requireLiveAnswer: confirmingWrite });

/**
 * The `?view=all&{kind}Id=` read: every open action in the container, in one request —
 * and since 029, ordered by each action's primary tag (`sort=tag`, issue 109).
 *
 * `sort` is orthogonal to `view`: `view` says which actions, `sort` says in what order. Both
 * container kinds send it, because this one read serves both screens — which is what makes
 * "context and project alike" structural rather than a rule someone has to remember.
 *
 * The ordering is still entirely the SERVER's (006 FR-017). Nothing here sorts; this asks.
 *
 * 036 (issue 221): `confirmingWrite` sets `requireLiveAnswer` — every caller confirming a write that
 * just happened (a tick, a move, a capture) passes `true`; `load()` and the quiet refresh leave it
 * `false`, so either may fall back to a stored view.
 */
const readActionsAll = (kind: ContainerKind, id: number, confirmingWrite = false): Promise<PagedActions> =>
  appClient().get<PagedActions>(containerViewUrls(kind, id)[1]!, { requireLiveAnswer: confirmingWrite });

/**
 * The `?view=completed&{kind}Id=` read. How many it asks for is the practitioner's
 * one completions preference, not a number of this screen's own (009 FR-007) — and it is the
 * *fetch* size, which holds more than the screen shows so "show more" has something to reveal.
 * Before 009 this asked for exactly five and so could never show a sixth.
 *
 * It deliberately does NOT send 029's `sort=tag`, and that omission is load-bearing rather than
 * an oversight (spec FR-009): this is a short newest-first tail of what was just finished, and
 * grouping it by tag would destroy the "recently" reading that is the whole point of it. The
 * server would accept the combination — it is legal on every view — so nothing but this
 * omission stops it, which is why `container.store.spec.ts` asserts the parameters this read
 * issues rather than trusting that they are currently right.
 *
 * 036 (issue 221): `confirmingWrite`, same reasoning as `readActionsAll`'s own note.
 */
const readActionsCompleted = (kind: ContainerKind, id: number, confirmingWrite = false): Promise<PagedActions> =>
  appClient().get<PagedActions>(containerViewUrls(kind, id)[2]!, { requireLiveAnswer: confirmingWrite });

/** FR-018/FR-024 — whether any of the CURRENTLY-SHOWN container's own reads is drawn from storage. */
export const containerStoredView = {
  use: (): StoredViewSummary => {
    const data = containerData.use();
    return useShownFromStorage(data ? containerViewUrls(data.kind, data.container.id) : []);
  },
  get: (): StoredViewSummary => {
    const data = dataNow();
    return shownFromStorageNow(data ? containerViewUrls(data.kind, data.container.id) : []);
  },
};

/** FR-035, research R-022 — false while any of the shown container's reads is drawn from storage.
 *  Delete (the row's and the container's own), the settings panels and export are all absent under
 *  this; FR-026's own controls (complete, star, defer, edit) stay. */
export function useContainerWritable(): boolean {
  const data = containerData.use();
  return useWritable(data ? containerViewUrls(data.kind, data.container.id) : []);
}

/**
 * The fourth and fifth fixed requests: both container lists (see `ContainerData`'s comment for
 * why both). Built once at module scope and kept, never rebuilt per load.
 */
const readLookups = containerLookupsReader();

/** The four fixed requests `load()` and a quiet refresh both need, for whichever container the
 *  caller names — the one place they are combined into `ContainerData`. */
const readContainerData = async (kind: ContainerKind, id: number): Promise<ContainerData> => {
  const [container, all, completed, lookups] = await Promise.all([
    readContainer(kind, id), readActionsAll(kind, id), readActionsCompleted(kind, id), readLookups(),
  ]);
  return kind === 'project'
    ? {
        kind: 'project', container: container as ProjectSummary, actions: all.items,
        completedRecent: completed.items, contexts: lookups.contexts, projects: lookups.projects,
      }
    : {
        kind: 'context', container: container as ContextSummary, actions: all.items,
        completedRecent: completed.items, contexts: lookups.contexts, projects: lookups.projects,
      };
};

/**
 * A capture refused while this page is still showing the SAME container or context it was typed on
 * can be shown here and removed from the outbox (`dismissRefused`); one refused after the
 * practitioner navigated away — to another screen, or to a DIFFERENT container/context — must not
 * surface `problem` on a page the line was never typed on (`core/reach/mount-guard.ts`).
 */
const containerMountGuard = createMountGuard<{ readonly kind: ContainerKind; readonly id: number }>(
  (a, b) => a.kind === b.kind && a.id === b.id,
);
export const registerContainerMounted = containerMountGuard.register;

/** `store.setState`'s ready-data half, with no side effect on the quiet-refresh generation. */
const applyReady = (data: ContainerData): void => store.setState({ status: { kind: 'ready', data } });

/**
 * `core/reach/quiet-refresh.ts` — see that file for the mechanism. This store needs it for a second
 * reason beyond racing a write: without it, a stale answer for the container the practitioner just
 * navigated AWAY from could land on the different container they navigated TO — both read `ready`.
 * `load()` touching the guard on every navigation (below) closes both gaps in the same mechanism.
 */
const quietRefresh = createQuietRefresh<ContainerData>({
  ready: () => store.getState().status.kind === 'ready',
  busy: () => store.getState().pending.length > 0,
  read: () => {
    const data = dataNow()!; // `ready()` already established this is non-null
    return readContainerData(data.kind, data.container.id);
  },
  apply: applyReady,
  busyDiscard: () => {
    const data = dataNow()!; // `ready()` already established this is non-null
    restoreLastCleared(containerViewUrls(data.kind, data.container.id));
  },
});

/** Every local change to the screen's data goes through this, so the quiet refresh always knows one
 *  happened. */
const setReady = (data: ContainerData): void => {
  quietRefresh.touched();
  applyReady(data);
};

/**
 * Adapts this store to `shared/actions/optimistic.ts`'s write shape (research R-008), the
 * same port `home.store.ts` uses — see that file's `writeHost` for the fuller comment.
 * `data` is safe as a bare non-null assertion: `mutate()` only ever calls it after `isReady()` has
 * already been checked true, and `dataOf` guarantees `dataNow()` is non-null whenever
 * `status.kind === 'ready'`.
 */
const writeHost: OptimisticHost<ContainerData> = {
  isReady: () => store.getState().status.kind === 'ready',
  pending: () => store.getState().pending,
  data: () => dataNow()!,
  setData: setReady,
  begin: (id) => {
    store.setState({ pending: [...store.getState().pending, id], problem: null });
  },
  fail: (problem) => store.setState({ problem }),
  release: (id) => {
    store.setState({ pending: store.getState().pending.filter((p) => p !== id) });
    quietRefresh.settled();
  },
};

/**
 * `mutate()`'s `reread` array shares one result type across every entry (`Reread<TData,
 * TRereadResult>`), but `deleteAction` below needs `view=all`, `view=completed` and the container
 * itself to fire *concurrently* (`Promise.all` inside `mutate()`) and fold back with three
 * different response shapes. A tagged union lets all three share one `Reread<ContainerData,
 * WriteReread>` type instead of widening `mutate()`'s own contract for every other caller
 * (`home.store.ts` included) to a type it never needs.
 */
type WriteReread =
  | { readonly kind: 'all'; readonly paged: PagedActions }
  | { readonly kind: 'completed'; readonly paged: PagedActions }
  | { readonly kind: 'container'; readonly container: ProjectSummary | ContextSummary };

/**
 * Built per call, not once: unlike `home.store.ts`'s fixed re-reads, which of `view=all` /
 * `view=completed` / the container itself applies depends on the currently-loaded `kind`/id,
 * which is not fixed for the store's lifetime (a practitioner can navigate project → project).
 */
const rereadAllFor = (kind: ContainerKind, id: number): Reread<ContainerData, WriteReread> => ({
  read: async () => ({ kind: 'all', paged: await readActionsAll(kind, id, true) }),
  apply: (data, result) => (result.kind === 'all' ? { ...data, actions: result.paged.items } : data),
});
const rereadCompletedFor = (kind: ContainerKind, id: number): Reread<ContainerData, WriteReread> => ({
  read: async () => ({ kind: 'completed', paged: await readActionsCompleted(kind, id, true) }),
  apply: (data, result) => (result.kind === 'completed' ? { ...data, completedRecent: result.paged.items } : data),
});
/** Keeps `container.counts` fresh after a write that can change how many actions the container holds. */
const rereadContainerFor = (kind: ContainerKind, id: number): Reread<ContainerData, WriteReread> => ({
  read: async () => ({ kind: 'container', container: await readContainer(kind, id, true) }),
  apply: (data, result) => {
    if (result.kind !== 'container') return data;
    return data.kind === 'project'
      ? { ...data, container: result.container as ProjectSummary }
      : { ...data, container: result.container as ContextSummary };
  },
});

const without = (list: readonly ActionSummary[], id: number): readonly ActionSummary[] => list.filter((a) => a.id !== id);

/**
 * Puts a row back where it was. By position rather than by re-sorting: the order is the server's
 * (FR-017), so the page's only honest guess about where a row belongs is where it just was.
 */
const restore = (list: readonly ActionSummary[], action: ActionSummary, at: number): readonly ActionSummary[] =>
  list.some((a) => a.id === action.id) ? list : [...list.slice(0, at), action, ...list.slice(at)];

/** A row can be open or recently-completed on this page (unlike home's single `available` list). */
const replaceAnywhere = (data: ContainerData, updated: ActionSummary): ContainerData => ({
  ...data,
  actions: data.actions.map((a) => (a.id === updated.id ? updated : a)),
  completedRecent: data.completedRecent.map((a) => (a.id === updated.id ? updated : a)),
});

export function dismissProblem(): void {
  store.setState({ problem: null });
}

/**
 * The whole detail page, in four fixed requests (FR-005/SC-003) — never one per action,
 * whatever the container holds. Fired together: none depends on another, and the page is only
 * useful once all four have answered. `Promise.allSettled` rather than `Promise.all` because a
 * 404 from *the container* specifically means something different (not-found) from a 404 or
 * 500 from any of the other three (an error banner) — both must be told apart, so both must be
 * observed rather than short-circuiting on whichever one rejects first.
 */
export async function load(kind: ContainerKind, id: number): Promise<void> {
  // A fresh load (including a navigation to a DIFFERENT container/context) supersedes anything a
  // quiet refresh has in flight, whether or not `status` ever leaves `ready`.
  quietRefresh.touched();
  // 036 (issue 222, FR-023) — "written when a page is opened": fire-and-forget, so a device that
  // cannot store this loses only the ORDERING `fetch-ahead.ts` would have used, never this load.
  void recordPageOpened(kind, id);
  /**
   * **This reproduces a lifetime, not a caching decision.** In Angular `ContainerStore` was
   * `providers: [ContainerStore]` on the page — one instance per component — and the default route
   * reuse strategy keeps that component across `/projects/1` → `/projects/2` (same route config) but
   * replaces it across `/projects/1` → `/contexts/1` (different config). So the previous container's
   * rows were carried into the loading state of a same-kind navigation and NOT of a cross-kind one,
   * which is visible: the page renders in full whenever `data` is non-null, showing a progress bar
   * only when it is not. A module store outlives both, so the kind comparison is what puts the
   * distinction back.
   */
  const loaded = dataNow();
  if (loaded && loaded.kind !== kind) store.setState(EMPTY);
  const carried = loaded && loaded.kind === kind ? loaded : null;
  // 010 FR-006: the one reduction on this page's path.
  const instant = clock();
  store.setState({
    status: { kind: 'loading', data: carried },
    today: calendarDayIn(instant, effectiveZone.get()), now: instant,
    // 036 (issue 234): set before any of the four reads below is even attempted, so a capture typed
    // while this very load is failing — or before it has ever succeeded once — still has a container
    // to attach to. See `ContainerState.requested`'s own comment.
    requested: { kind, id },
  });

  const [containerResult, allResult, completedResult, lookupsResult] = await Promise.allSettled([
    readContainer(kind, id),
    readActionsAll(kind, id),
    readActionsCompleted(kind, id),
    readLookups(),
  ]);

  if (containerResult.status === 'rejected') {
    const error = toApiError(containerResult.reason);
    // R-005: the detail page renders the shared NotFound component in place of an error
    // banner — a stale link and the API being down are not the same failure.
    store.setState(error.status === 404
      ? { status: { kind: 'not-found' } }
      : { status: { kind: 'error', error, data: carried } });
    return;
  }
  if (allResult.status === 'rejected') {
    store.setState({ status: { kind: 'error', error: toApiError(allResult.reason), data: carried } });
    return;
  }
  if (completedResult.status === 'rejected') {
    store.setState({ status: { kind: 'error', error: toApiError(completedResult.reason), data: carried } });
    return;
  }
  if (lookupsResult.status === 'rejected') {
    store.setState({ status: { kind: 'error', error: toApiError(lookupsResult.reason), data: carried } });
    return;
  }

  // `readContainer` returns a `kind`-dependent type the ternary at its call site can't narrow
  // from a runtime parameter; `kind` here is the same value already used to build the request,
  // so these casts describe a fact already true, not a new assumption. The two lists need none:
  // both arms carry both, and `readLookups` answers with one shape.
  const lookups = lookupsResult.value;
  applyReady(kind === 'project'
    ? {
        kind: 'project',
        container: containerResult.value as ProjectSummary,
        actions: allResult.value.items,
        completedRecent: completedResult.value.items,
        contexts: lookups.contexts,
        projects: lookups.projects,
      }
    : {
        kind: 'context',
        container: containerResult.value as ContextSummary,
        actions: allResult.value.items,
        completedRecent: completedResult.value.items,
        contexts: lookups.contexts,
        projects: lookups.projects,
      });
}

/**
 * The QUIET re-read `resync` (FR-036) must trigger, not `load()` itself. See `home.store.ts`'s
 * `quietRefreshHome` for the fuller reasoning. This re-reads the SAME four requests `load()` does,
 * for whichever container is CURRENTLY shown (read off `status.data` itself, never a stale
 * parameter), and replaces the data only once every one of them has answered — never touching
 * `status`'s kind, `today`/`now`, `pending` or `problem`. Any failure at all is treated alike, and
 * quietly — there is no `not-found` transition to protect here, unlike `load()`.
 */
export const quietRefreshContainer = quietRefresh.run;

/**
 * Settings writes patch and re-read — but not the container: `PATCH /projects/{id}`
 * already returns the fresh `ProjectSummary`, counts included, so there is nothing about the
 * container itself left to re-read. What can go stale is the **actions**: research R-003 makes
 * on-hold a property of the container's own state, so only a `state` change can move one of
 * this project's actions between sections — a rename or a description edit changes nothing
 * `sections.ts` partitions on. Re-reading unconditionally would be a request FR-005 forbids for
 * no observable benefit; re-reading only on `state` keeps the promise while still making
 * SC-005 possible (a hide/complete's on-hold actions appear without a page reload).
 *
 * A refused write (wrong name conflict, or a foreign `defaultContextId` — a 404 per R-006,
 * *not* the container vanishing) becomes `problem`, leaving `status` exactly as it was: only
 * `load`'s own 404 means "gone" (R-005).
 */
export async function updateProjectSettings(patch: ProjectSettingsPatch): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready' || status.data.kind !== 'project') return;
  if (Object.keys(patch).length === 0) return; // the server 400s an empty merge-patch body (project.nothing_to_change)

  store.setState({ problem: null });
  try {
    const updated = await appClient().patch<ProjectSummary>(`${projectsUrl()}/${status.data.container.id}`, patch);
    let data: ContainerData = { ...status.data, container: updated };
    if ('state' in patch) {
      const all = await readActionsAll('project', updated.id);
      data = { ...data, actions: all.items };
    }
    setReady(data);
  } catch (error) {
    store.setState({ problem: toApiError(error) });
  }
}

/**
 * The weekly-review stamp (FR-009, R-006). A command with an empty body, not a patch: the
 * server owns the clock — `ProjectEndpoints.cs` exposes `POST /projects/{id}/reviewed`
 * precisely so a client cannot back-date a review — and answers with the fresh summary
 * carrying the new date and its counts.
 *
 * No re-read: reviewing a project moves no action between sections, so unlike a state change
 * this leaves the four lists exactly as they are. A refusal (a foreign id 404s) becomes
 * `problem`, never a not-found state — only `load`'s own 404 means the container is gone.
 */
export async function markReviewed(): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready' || status.data.kind !== 'project') return;

  store.setState({ problem: null });
  try {
    const updated = await appClient().post<ProjectSummary>(`${projectsUrl()}/${status.data.container.id}/reviewed`, null);
    setReady({ ...status.data, container: updated });
  } catch (error) {
    store.setState({ problem: toApiError(error) });
  }
}

/** The context mirror of `updateProjectSettings`: `isActive` is the only state-changing field (R-002/R-003). */
export async function updateContextSettings(patch: ContextSettingsPatch): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready' || status.data.kind !== 'context') return;
  if (Object.keys(patch).length === 0) return;

  store.setState({ problem: null });
  try {
    const updated = await appClient().patch<ContextSummary>(`${contextsUrl()}/${status.data.container.id}`, patch);
    let data: ContainerData = { ...status.data, container: updated };
    if ('isActive' in patch) {
      const all = await readActionsAll('context', updated.id);
      data = { ...data, actions: all.items };
    }
    setReady(data);
  } catch (error) {
    store.setState({ problem: toApiError(error) });
  }
}

/**
 * Folds back the action a tag write returned (011 FR-024). The write itself belongs to the
 * row's editor, which holds the whole set and `PUT`s it.
 *
 * **029 re-reads the open list here, and the three sibling `adoptAction`s deliberately do not.**
 * All four used to carry the same note — *"Nothing is re-read, deliberately. No ordering rule
 * and no availability rule reads a tag, so tagging one cannot move it."* On THIS page that
 * sentence is now false: `readActionsAll` asks for `sort=tag`, so an action's primary tag is the
 * second ordering key, and adding, removing or renaming a tag can move the row. Without the
 * re-read, tagging `#apple` onto the last row of a context leaves it last until a reload — the
 * page showing an order the server did not give it, which is exactly what 006 FR-017 and this
 * feature's own FR-001 forbid.
 *
 * It is the same reasoning `editSaved` records one method down, and the same shape: `foldIn`
 * rather than `mutate`, because the write has already succeeded (016's finding — `mutate`'s
 * already-pending guard would DISCARD a concurrent edit).
 *
 * Only `rereadAllFor`: a tag write cannot complete or reopen an action, so `completedRecent` is
 * untouched — and that list carries no `sort` anyway (spec FR-009).
 *
 * The other three `adoptAction`s (home, tag view, archive) keep the no-re-read note, and it is
 * still true of them: none of those screens requests `sort=tag`. If one ever does, it needs this
 * change too, and the note in each is what should be read first.
 */
export function adoptAction(updated: ActionSummary): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const kind = status.data.kind;
  const id = status.data.container.id;

  return foldIn(writeHost, updated.id, (data) => replaceAnywhere(data, updated), [
    rereadAllFor(kind, id),
  ]);
}

/**
 * One line of Tracks markup, sent with *this* container as the server-side default
 * (`defaultContextId`/`defaultProjectId`) — research R-007: the line the practitioner typed is
 * forwarded exactly as typed, never rewritten with an appended `@context`/`~project` marker to
 * simulate what the container already implies. Both open views are re-read afterwards, mirroring
 * `home.store.ts`'s `quickAdd`: where the new row lands in the order, and whether it is even in
 * the open set at all (a `>date` line can defer it on arrival), is the server's call.
 *
 * **036 (issue 207): routed through `submit()`, exactly as `home.store.ts`'s `quickAdd` is** — see
 * that function's header for what each of `submit`'s outcomes does here. No longer conditioned on
 * `status.kind === 'ready'` (research F-005); `dataNow()` is what `status.kind === 'error'` already
 * carries forward, so a capture typed while a reload is failing still has a container to attach to.
 *
 * **036 (issue 234): the one case the paragraph above USED to say this could not serve — the very
 * first load never having produced any data at all — is exactly the no-stored-copy screen this
 * container page renders in that case, and FR-015 says capture must work there too.** `data` is
 * still `null` then, but `ContainerState.requested` was set before `load()` ever attempted a read,
 * so `target` below falls back to it: the container's `kind`/`id` are the route's own, never the
 * server's answer, so there is no reason capture needed the read to have succeeded first.
 *

 * **The capture is always submitted, but its re-read is only ever applied while
 * the store is STILL `ready` for the SAME container it was submitted against.** Two ways this page
 * differs from `home.store.ts`'s version of this: `setReady` unconditionally sets
 * `status: 'ready'` (the same hazard `setData` has there), AND this screen can navigate to a
 * DIFFERENT container entirely while a capture is in flight — landing that container's fresh
 * `all`/`completed` reads on whatever container the practitioner has since moved to would show one
 * container's actions on another's page.
 */
/**
 * `capturing` guards this function itself, independent of `status`; see
 * `home.store.ts`'s `quickAdd` header for the fuller comment (the identical shape applies here,
 * issue 257 A3 included: `capturing` clears the moment the line is durably queued, not once the send
 * outcome is known).
 */
export async function quickAdd(line: string): Promise<void> {
  const text = line.trim();
  if (text.length === 0) return;
  if (store.getState().capturing) return;
  const data = dataNow();
  // 036 (issue 234): `data`'s own kind/id when it exists; otherwise the route's own, as `load()`
  // recorded it before either of its reads was even attempted (`ContainerState.requested`). Only
  // truly absent before this page has ever loaded anything at all (the pre-`load()` `idle` state).
  const target = data ? { kind: data.kind, id: data.container.id } : store.getState().requested;
  if (!target) return;
  const { kind: submittedKind, id: submittedId } = target;
  const stillTheSameContainer = (): boolean => {
    const now = dataNow();
    return store.getState().status.kind === 'ready' && now !== null
      && now.kind === submittedKind && now.container.id === submittedId;
  };

  store.setState({ problem: null, capturing: true });
  let queued = false;
  let created: QuickCaptureResult;
  try {
    const body: QuickAddRequest = submittedKind === 'project'
      ? { line: text, defaultProjectId: submittedId }
      : { line: text, defaultContextId: submittedId };
    const outcome = await submitCapture<QuickCaptureResult>(body, {
      onQueued: () => {
        queued = true;
        store.setState({ capturing: false });
      },
    });
    if (outcome.kind === 'waiting') return;
    if (outcome.kind === 'refused') {
      // Shown here, and the row removed from the outbox, only while this is still the page showing
      // the SAME container it was typed on — otherwise the row stays refused so the unsent count
      // still tells the practitioner (`core/reach/mount-guard.ts`).
      if (containerMountGuard.isCurrent({ kind: submittedKind, id: submittedId })) {
        store.setState({ problem: outcome.problem });
        void dismissRefused(outcome.seq, outcome.practitionerId);
      }
      return;
    }
    created = outcome.result;
  } catch (error) {
    store.setState({ problem: toApiError(error) });
    return;
  } finally {
    if (!queued) store.setState({ capturing: false });
  }

  if (!stillTheSameContainer()) return;

  try {
    // `submittedKind`/`submittedId`, never the closure `data` — `data` is `null` on exactly the
    // no-stored-copy path this comment above describes, and `stillTheSameContainer()` reads the
    // STORE's current data fresh, so it can turn true later (a concurrent load finishing while this
    // was in flight) even though `data` here never was anything to dereference.
    const [all, completed] = await Promise.all([
      readActionsAll(submittedKind, submittedId, true),
      readActionsCompleted(submittedKind, submittedId, true),
    ]);
    if (!stillTheSameContainer()) return;
    const current = dataNow()!;
    setReady(current.kind === 'project'
      ? {
          ...current,
          contexts: created.createdContext ? [...current.contexts, created.createdContext] : current.contexts,
          actions: all.items,
          completedRecent: completed.items,
        }
      : {
          ...current,
          projects: created.createdProject ? [...current.projects, created.createdProject] : current.projects,
          actions: all.items,
          completedRecent: completed.items,
        });
  } catch (error) {
    if (stillTheSameContainer()) store.setState({ problem: toApiError(error) });
  }
}

/**
 * Completing or reopening (036 T092, routed through the outbox — `queued-change.ts`'s header
 * comment has the two review findings this closes). **No optimistic move**: constitution II / issue
 * 209's rule is that a row shows the practitioner's own change and nothing that follows from it, so
 * the row stays exactly where it is until the write actually lands — the ticked appearance while
 * unsent is `action-row.tsx`'s overlay (T091), not this store's data. Both open and completed views
 * are re-read once it does — completing one action can unblock another (`Dependency`), and a
 * reopen's resulting section is the server's `status` to say, never this store's to guess (research
 * R-003).
 */
export function setCompleted(action: ActionSummary, completed: boolean): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const kind = status.data.kind;
  const id = status.data.container.id;

  return submitOrWithdraw(
    writeHost,
    action.id,
    'isCompleted',
    { isCompleted: completed },
    { isCompleted: completed, description: action.description },
    (current) => current,
    [rereadAllFor(kind, id), rereadCompletedFor(kind, id)],
  ).then(() => undefined);
}

/**
 * 016 FR-017/FR-018: the row's editor has saved. The PATCH was its own (a refusal belongs beside
 * the form, FR-010), so what arrives here is a write that already succeeded — see
 * `home.store.ts`'s `editSaved` for the fuller note, and `foldIn` for why that is not a `mutate`.
 *
 * This is the page where the consequence is visible: an action moved into another project is no
 * longer part of *this* container's `view=all` answer, so both views are re-read and the row
 * leaves without a reload. `adoptAction` — the tag path — would have left it sitting on a page
 * it no longer belongs to.
 */
export function editSaved(updated: ActionSummary): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const kind = status.data.kind;
  const id = status.data.container.id;

  return foldIn(writeHost, updated.id, (data) => replaceAnywhere(data, updated), [
    rereadAllFor(kind, id),
    rereadCompletedFor(kind, id),
  ]);
}

/**
 * A star marks an action (036 T092, routed through the outbox); it changes neither its order nor its
 * availability, so — same as `home.store.ts` — nothing is re-read once it lands. No optimistic move:
 * the starred appearance while unsent is the overlay's (T091), same reasoning as `setCompleted` above.
 */
export function toggleStar(action: ActionSummary): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const isStarred = !action.isStarred;
  return submitOrWithdraw(
    writeHost,
    action.id,
    'isStarred',
    { isStarred },
    { isStarred },
    (data, { action: updated }) => replaceAnywhere(data, updated),
    [],
  ).then(() => undefined);
}

/**
 * Deferring (036 T092, routed through the outbox). Already had no optimistic list edit before this
 * slice — this store's four sections all live inside one `actions` array partitioned client-side by
 * `sections.ts`, and where a deferred row lands next is exactly the server-derived `status` this
 * store must never guess (research R-003) — so routing it through the outbox changes nothing about
 * what the data does, only that it now works with no connection. Only `view=all` is re-read once the
 * write lands: a defer can never move a row into or out of `completedRecent`.
 */
export function defer(action: ActionSummary, deferUntil: string | null): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const kind = status.data.kind;
  const id = status.data.container.id;

  return submitOrWithdraw(
    writeHost,
    action.id,
    'deferUntil',
    { deferUntil },
    { deferUntil },
    (current) => current,
    [rereadAllFor(kind, id)],
  ).then(() => undefined);
}

/**
 * Deleting one action. Both open views are re-read (a delete can
 * unblock a dependent the same way a completion can), plus the container itself: `counts.total`
 * is what the empty-container delete affordance gates on, and this is the one write that can
 * take it to zero without a full page reload.
 */
export function deleteAction(action: ActionSummary): Promise<void> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return Promise.resolve();
  const data = status.data;
  const wasAt = data.actions.findIndex((a) => a.id === action.id);
  const kind = data.kind;
  const id = data.container.id;

  return mutate(
    writeHost,
    action.id,
    (current) => ({ ...current, actions: without(current.actions, action.id) }),
    (current) => ({ ...current, actions: restore(current.actions, action, wasAt) }),
    () => appClient().delete<void>(`${actionsUrl()}/${action.id}`),
    (current) => current,
    [rereadAllFor(kind, id), rereadCompletedFor(kind, id), rereadContainerFor(kind, id)],
  );
}

/**
 * Deleting the whole container (the empty-container affordance) — not routed through
 * `mutate()`: there is no per-row id, and once this succeeds there is nothing left on this page
 * to keep in sync, so the caller (the page) is expected to navigate away rather than reload.
 * Resolves `true`/`false` rather than throwing, so the page can decide what to do without a
 * try/catch of its own; a refusal (the container is not actually empty, R-004) becomes `problem`.
 */
export async function deleteContainer(): Promise<boolean> {
  const status = store.getState().status;
  if (status.kind !== 'ready') return false;
  const data = status.data;

  store.setState({ problem: null });
  try {
    const url = data.kind === 'project' ? `${projectsUrl()}/${data.container.id}` : `${contextsUrl()}/${data.container.id}`;
    await appClient().delete<void>(url);
    return true;
  } catch (error) {
    store.setState({ problem: toApiError(error) });
    return false;
  }
}

/** Test seam, like the other stores'. Production code never calls it. */
export function resetContainer(): void {
  store.setState(EMPTY);
  quietRefresh.reset();
  containerMountGuard.reset();
}
